import { useMemo } from "react";
import { Dayjs } from "dayjs";
import { useQuery } from "@apollo/client";
import { graphql } from "../../gql";
import { OnboardingLiveListPeriod } from "../../gql/graphql.ts";
import { useOnboardingLives } from "../useOnboardingLives.ts";
import {
  OVERALL_STATISTICS_FROM,
  OVERALL_STATISTICS_PERIOD,
  overallStatisticsPeriodOf,
  StatisticsPeriodChoice,
  statisticsPeriodOf,
} from "./onboardingLiveStatisticsPeriod.ts";
import { computeOnboardingLiveStatistics } from "./onboardingLiveStatistics.ts";

const GetOnboardingLiveStatisticsRecordsQuery = graphql(`
  query getOnboardingLiveStatisticsRecords($fromUtc: String!, $toUtc: String!) {
    getOnboardingLiveStatisticsRecords(fromUtc: $fromUtc, toUtc: $toUtc) {
      lives {
        id
        startUtc
        teacherId
        participantsCount
        hasEnded
        feedback {
          participantsCount
        }
      }
      deletedLives {
        id
        participantsCount
      }
      cancellations {
        onboardingLiveId
        cancelledBy
      }
      conductorChanges {
        onboardingLiveId
        fromTeacherId
      }
    }
  }
`);

const GetOnboardingLiveFunnelStatisticsQuery = graphql(`
  query getOnboardingLiveFunnelStatistics($fromUtc: String!, $toUtc: String!) {
    getOnboardingLiveFunnelStatistics(fromUtc: $fromUtc, toUtc: $toUtc) {
      usersWithOnboardingLiveToBeDone
      usersWaitingForMoreThanAWeek
      usersWhoCompletedOnboardingLive
      averageDaysToCompleteOnboardingLive
      medianDaysToCompleteOnboardingLive
    }
  }
`);

function firstStartOf(lives: { startUtc: string }[]): string | undefined {
  return lives.reduce<string | undefined>(
    (first, live) => (!first || live.startUtc < first ? live.startUtc : first),
    undefined,
  );
}

export function useOnboardingLiveStatistics(
  periodChoice: StatisticsPeriodChoice,
  now: Dayjs,
) {
  const isOverall = periodChoice === OVERALL_STATISTICS_PERIOD;
  const variables = {
    fromUtc: (isOverall
      ? OVERALL_STATISTICS_FROM
      : statisticsPeriodOf(periodChoice, now).from
    ).toISOString(),
    toUtc: now.toISOString(),
  };
  const records = useQuery(GetOnboardingLiveStatisticsRecordsQuery, {
    variables,
    fetchPolicy: "cache-and-network",
  });
  const funnel = useQuery(GetOnboardingLiveFunnelStatisticsQuery, {
    variables,
    fetchPolicy: "cache-and-network",
  });
  const upcoming = useOnboardingLives({
    period: OnboardingLiveListPeriod.Upcoming,
  });

  const recordsData = records.data?.getOnboardingLiveStatisticsRecords;
  const period = useMemo(
    () =>
      isOverall
        ? overallStatisticsPeriodOf(firstStartOf(recordsData?.lives ?? []), now)
        : statisticsPeriodOf(periodChoice, now),
    [isOverall, periodChoice, recordsData, now],
  );
  const statistics = useMemo(
    () =>
      recordsData
        ? computeOnboardingLiveStatistics({ ...recordsData, period })
        : undefined,
    [recordsData, period],
  );

  return {
    period,
    statistics,
    loadingStatistics: !statistics,
    funnel: funnel.data?.getOnboardingLiveFunnelStatistics,
    upcomingLives: upcoming.onboardingLives,
    loadingUpcomingLives:
      upcoming.loading && upcoming.onboardingLives.length <= 0,
  };
}
